/* eslint-disable no-empty */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';

import DeleteIcon from '@/assets/all-icons/delete-icon';
import SettingsIcon from '@/assets/all-icons/settings-icon';
import config from '@/config';
import useStateHandler from '@/hooks/useStateHandler';
import ChannelsService from '@/services/channels.service';
import ContinueButton from '@/shared-components/buttons/components/ContinueButton';
import UserProfileImage from '@/shared-components/cards/components/UserProfileImage';
import BackArrowIcon from '@/shared-components/icons/components/public/BackArrowIcon';
import ThreeDotsHorizontal from '@/shared-components/icons/components/public/styles/ThreeDotsHorizontal';
import XIcon from '@/shared-components/icons/components/public/XIcon';
import Loader from '@/shared-components/loader/Loader';
import GenericPopup from '@/shared-components/popups/components/GenericPopup';
import { formatCurrencyAmount } from '@/utils/currency.utils';
import { computeSubState, formatDate } from '@/utils/subscription';

import InviteChannelPopup from './components/InviteChannelPopup';
import { resolveChannelOwnerProfileType, resolveImageId } from './utils/channelMessageHelpers';

import './styles/ChannelInfo.css';

/** Normalize CF Stream HLS URLs to MP4 when available */
function normalizeVideoUrl(url) {
  if (!url || typeof url !== 'string') return url;
  try {
    const u = new URL(url, window.location.origin);
    const host = u.hostname;
    const isCFStream =
      /cloudflarestream\.com$/i.test(host) || /videodelivery\.net$/i.test(host);

    if (isCFStream && /\/manifest\/.+video\.m3u8$/i.test(u.pathname)) {
      // HLS manifest → try CF "downloads/default.mp4"
      return url.replace(/\/manifest\/.+video\.m3u8$/i, '/downloads/default.mp4');
    }
  } catch (_) {}
  return url;
}

function normalizeMediaItem(item, index) {
  if (!item) return null;
  const lookupString = (...candidates) =>
    candidates.find((value) => typeof value === 'string' && value.trim().length);
  const source = (item.media && typeof item.media === 'object') ? item.media : item;

  const getFromObject = (value) => {
    if (!value || typeof value !== 'object') return null;
    return lookupString(
      value.url,
      value.previewUrl,
      value.preview_url,
      value.secure_url,
      value.path,
      value.image,
      value.thumbnail,
      value.thumbnailUrl,
      value.thumbnail_url,
    );
  };

  // Normalize to /w=### for our CDNs
  const withWidth = (rawUrl, w = 256) => {
    if (!rawUrl) return null;
    if (/^(data:|blob:)/i.test(rawUrl)) return rawUrl;

    if (/\/w=\d+$/i.test(rawUrl)) return rawUrl.replace(/\/w=\d+$/i, `/w=${w}`);

    let url;
    try { url = new URL(rawUrl, window.location.origin); } catch { return rawUrl; }
    const host = url.hostname.toLowerCase();

    let imageHost = null;
    try {
      if (config?.app?.imageServiceDomain) {
        imageHost = new URL(config.app.imageServiceDomain).hostname.toLowerCase();
      }
    } catch { /* ignore */ }

    const isImageDelivery = host.endsWith('imagedelivery.net');
    const isOurImageHost  = !!imageHost && host === imageHost;

    const path = url.pathname;
    const parts = path.split('/').filter(Boolean);

    if (isImageDelivery) {
      if (parts.length >= 3) {
        parts[parts.length - 1] = `w=${w}`;
        url.pathname = '/' + parts.join('/');
        url.search = '';
        return url.toString();
      }
      url.pathname = path.replace(/\/$/, '') + `/w=${w}`;
      url.search = '';
      return url.toString();
    }

    if (isOurImageHost) {
      url.pathname = path.replace(/\/$/, '') + `/w=${w}`;
      url.search = '';
      return url.toString();
    }

    const sep = url.search ? '&' : '?';
    url.search = (url.search || '') + `${sep}w=${w}`;
    return url.toString();
  };

  const playbackUrl = lookupString(
    source.playbackUrl,
    source.playback_url,
    source.deliveryUrl,
    source.delivery_url,
  );

  const mediaUrl = playbackUrl || lookupString(
    source.url,
    source.secure_url,
    source.fileUrl,
    source.file_url,
    source.path,
    source.location,
    getFromObject(source.file),
    getFromObject(source.asset),
  );

  const previewRaw = lookupString(
    source.preview,
    source.previewUrl,
    source.preview_url,
    source.thumbnail,
    source.thumbnailUrl,
    source.thumbnail_url,
    source.image,
    getFromObject(source.preview),
    getFromObject(source.thumbnail),
  ) || mediaUrl;

  const rawType = lookupString(
    source.type,
    source.mediaType,
    source.media_type,
    source.kind,
    source.mimeType,
    source.mime_type,
    item?.message_type,
  ) || '';

  let normalizedType = rawType;
  if (rawType.includes('/')) normalizedType = rawType.split('/')[0];
  else if (rawType.includes(':')) {
    const parts = rawType.split(':'); normalizedType = parts[parts.length - 1];
  }

  const id = String(item._id || item.id || source.assetId || source.asset_id || index);
  const type = (normalizedType?.toLowerCase() || 'image');

  // grid -> 512; lightbox -> 1600
  const previewUrl   = type === 'image' ? withWidth(previewRaw, 512)             : previewRaw;
  const thumbnailUrl = type === 'image' ? withWidth(previewRaw, 512)             : previewRaw;
  const imageUrl     = type === 'image' ? withWidth(mediaUrl || previewRaw,1200) : (mediaUrl || previewRaw);
  const fullUrl      = type === 'image' ? withWidth(mediaUrl || previewRaw,1600) : (mediaUrl || previewRaw);

  return {
    id,
    type,
    url: imageUrl || previewUrl,
    fullUrl,
    playbackUrl: playbackUrl || null,
    previewUrl,
    thumbnailUrl,
    assetId: source.assetId || source.asset_id || null,
  };
}

function normalizeMember(entry, index) {
  if (!entry) return null;
  const lookupString = (...candidates) =>
    candidates.find((value) => typeof value === 'string' && value.trim().length);
  const source = entry.user || entry.member || entry.profile || entry;

  const displayName = lookupString(
    entry.displayName,
    entry.name,
    source?.displayName,
    source?.name,
    source?.fullName,
    source?.artist_name,
    source?.gallery_name,
  ) || lookupString(source?.username, source?.user_username, entry.username) || `Member ${index + 1}`;

  const username = lookupString(source?.user_username, source?.username, entry.username);

  const avatar = resolveImageId(
    entry.avatar,
    source?.avatar,
    source?.user_image,
    source?.profile_image,
  );

  const role = lookupString(
    entry.membershipRole,
    entry.role,
    entry.memberRole,
    entry.membership_role,
    entry.member_role,
  );

  const id = String(entry._id || source?._id || username || index);
  const membershipId = entry?._id ? String(entry._id) : id;
  const userId = source?._id ? String(source._id) : null;
  const profileType = resolveChannelOwnerProfileType(
    source?.typeOfAccount
    || source?.type_of_account
    || source?.user_type
    || source?.__t
    || entry.typeOfAccount
    || entry.type_of_account
    || entry.user_type
    || entry.__t,
    'collector'
  );

  return {
    id,
    membershipId,
    userId,
    displayName,
    username,
    avatar,
    role: role ? role.toUpperCase() : null,
    profileType
  };
}

function resolveChannelAvatar(channelOverview) {
  if (!channelOverview) return null;
  return resolveImageId(
    channelOverview?.owner?.user_image,
    channelOverview?.owner?.profile_image,
    channelOverview?.owner_image,
    channelOverview?.user_image,
  );
}

function resolveChannelVideo(channelOverview) {
  if (!channelOverview) return null;
  const lookupString = (...candidates) =>
    candidates.find((value) => typeof value === 'string' && value.trim().length);
  return lookupString(
    channelOverview.videoUrl,
    channelOverview.video_url,
    channelOverview.video,
    channelOverview.introVideo,
    channelOverview.intro_video,
    channelOverview.introVideoUrl,
    channelOverview.presentationVideo,
    channelOverview.galleryVideo,
  );
}

export default function ChannelInfo() {
  const { ownerUsername, user_username, creator_username, channelId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = useStateHandler();
  const resolvedOwner = ownerUsername || user_username || creator_username || '';
  const currentUsername = String(state?.user_session?.user_username || '');
  const currentUserId = state?.user_session?._id ? String(state.user_session._id) : null;
  const canManageChannel = Boolean(
    resolvedOwner
    && currentUsername
    && resolvedOwner.toLowerCase() === currentUsername.toLowerCase()
  );

  const [channelOverview, setChannelOverview] = useState(null);
  const [channelMembers, setChannelMembers] = useState([]);
  const [channelMedia, setChannelMedia] = useState([]);
  const [isMemberOfThis, setIsMemberOfThis] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [invitePopupOpen, setInvitePopupOpen] = useState(false);
  const [activeMemberMenuId, setActiveMemberMenuId] = useState(null);
  const [memberPendingRemoval, setMemberPendingRemoval] = useState(null);
  const [removingMemberId, setRemovingMemberId] = useState(null);
  const [headerMenuOpen, setHeaderMenuOpen] = useState(false);
  const [leaveChannelPopupOpen, setLeaveChannelPopupOpen] = useState(false);
  const [revokeSubscriptionPopupOpen, setRevokeSubscriptionPopupOpen] = useState(false);
  const [leavingChannel, setLeavingChannel] = useState(false);
  const [revokingSubscription, setRevokingSubscription] = useState(false);

  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxType, setLightboxType] = useState(null); // 'image' | 'video'
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [lightboxPoster, setLightboxPoster] = useState(null);

  const lightboxVideoRef = useRef(null);
  const [hlsInstance, setHlsInstance] = useState(null);

  const openLightbox = useCallback((item) => {
    if (!item) return;
    const type = item.type === 'video' ? 'video' : 'image';

    let src;
    if (type === 'video') {
      // Prefer playbackUrl (HLS/MP4) and normalize it
      const raw = item.playbackUrl || item.url || item.fullUrl || item.previewUrl;
      src = normalizeVideoUrl(raw);
    } else {
      src = item.fullUrl || item.url || item.previewUrl;
    }
    if (!src) return;

    setLightboxType(type);
    setLightboxSrc(src);
    setLightboxPoster(item.previewUrl || item.thumbnailUrl || null);
    setLightboxOpen(true);
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
    setLightboxType(null);
    setLightboxSrc(null);
    setLightboxPoster(null);
  }, []);

  // Esc to close
  useEffect(() => {
    if (!lightboxOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') closeLightbox(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxOpen, closeLightbox]);

  // --- DATA ---
  useEffect(() => {
    let isMounted = true;
    async function fetchData() {
      if (!channelId) {
        setChannelOverview(null);
        setChannelMembers([]);
        setChannelMedia([]);
        setIsMemberOfThis(false);
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const [overviewResult, membersResult, mediaResult, membershipResult] = await Promise.allSettled([
          ChannelsService.getOverview(channelId),
          ChannelsService.getMembersList(channelId),
          ChannelsService.getSharedMedia(channelId, { limit: 50 }),
          ChannelsService.getMemberOf('me'),
        ]);

        if (!isMounted) return;

        let overviewPayload = null;
        if (overviewResult.status === 'fulfilled' && overviewResult.value?.response?.ok) {
          overviewPayload = overviewResult.value.data;
        } else {
          try {
            const fallback = await ChannelsService.getOne(channelId);
            if (!isMounted) return;
            if (fallback?.response?.ok) overviewPayload = fallback.data;
          } catch { /* ignore */ }
        }

        const membersPayload = membersResult.status === 'fulfilled' && membersResult.value?.response?.ok
          ? membersResult.value.data : [];

        const mediaPayload = mediaResult.status === 'fulfilled' && mediaResult.value?.response?.ok
          ? mediaResult.value.data : [];

        if (!overviewPayload && !membersPayload?.length && !mediaPayload?.length) {
          setError('The channel information could not be loaded.');
        }

        setChannelOverview(overviewPayload || null);
        setChannelMembers(Array.isArray(membersPayload) ? membersPayload : []);
        setChannelMedia(Array.isArray(mediaPayload) ? mediaPayload : []);
        let memberFlag = false;
        if (membershipResult?.status === 'fulfilled' && membershipResult.value?.response?.ok) {
          const membershipData = membershipResult.value.data;
          const membershipList = Array.isArray(membershipData?.data)
            ? membershipData.data
            : Array.isArray(membershipData?.results)
              ? membershipData.results
              : Array.isArray(membershipData)
                ? membershipData
                : [];
          memberFlag = membershipList.some((entry) => String(entry?._id ?? entry?.id) === String(channelId));
        }
        setIsMemberOfThis(memberFlag);
      } catch {
        if (!isMounted) return;
        setError('An error occurred while fetching the channel information.');
        setChannelOverview(null);
        setChannelMembers([]);
        setChannelMedia([]);
        setIsMemberOfThis(false);
      } finally {
        if (isMounted) setLoading(false);
      }
    }
    fetchData();
    return () => { isMounted = false; };
  }, [channelId]);

  const subState = useMemo(() => computeSubState({
    overview: channelOverview,
    isMember: isMemberOfThis,
  }), [channelOverview, isMemberOfThis]);

  const {
    isPaidChannel,
    hasSub,
    expired,
    autoRenewOn,
    autoRenewOff,
    expiresMs,
    isMember,
  } = subState;

  const expiryLabel = useMemo(() => formatDate(expiresMs), [expiresMs]);
  const canViewRestrictedContent = canManageChannel || !isPaidChannel || isMember;
  const showExpiredNotice = isPaidChannel && hasSub && (!isMember || expired);
  const showAutoRenewOffNotice = isPaidChannel && hasSub && isMember && !expired && autoRenewOff;

  const channelName = useMemo(() => {
    if (channelOverview) {
      return (
        channelOverview.name ||
        channelOverview.title ||
        channelOverview.channelName ||
        channelOverview.channel_name
      );
    }
    return resolvedOwner ? `Channel by ${resolvedOwner}` : 'Channel';
  }, [channelOverview, resolvedOwner]);

  const channelType = useMemo(() => {
    if (!channelOverview) return null;
    return (
      channelOverview.type ||
      channelOverview.channelType ||
      channelOverview.channel_type ||
      channelOverview.membershipType
    );
  }, [channelOverview]);
  const isSubscribersType = String(channelType || '').toLowerCase() === 'subscribers';
  const revokeSubscriptionButtonLabel = useMemo(() => {
    const amount = Number(channelOverview?.price || 0);
    if (!Number.isFinite(amount) || amount <= 0) {
      return 'Cancel Subscription';
    }
    const formatted = formatCurrencyAmount(amount, channelOverview?.currency || 'USD', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return formatted
      ? `Cancel (${formatted}) Subscription`
      : `Cancel Subscription`;
  }, [channelOverview?.currency, channelOverview?.price]);

  const channelDescription = useMemo(() => {
    if (!channelOverview) return null;
    return (
      channelOverview.description ||
      channelOverview.summary ||
      channelOverview.about ||
      channelOverview.bio
    );
  }, [channelOverview]);

  const channelAvatar = useMemo(() => resolveChannelAvatar(channelOverview), [channelOverview]);
  const channelOwnerProfileType = useMemo(() => (
    resolveChannelOwnerProfileType(
      channelOverview?.channel_kind
      || channelOverview?.channelKind
      || channelOverview?.owner?.user_type
      || channelOverview?.owner?.__t
      || channelOverview?.ownerType
      || channelOverview?.channel?.owner?.user_type
      || channelOverview?.channel?.owner?.__t
      || (canManageChannel ? (state?.user_session?.user_type ?? state?.user_session?.__t ?? null) : null),
      'gallery'
    )
  ), [canManageChannel, channelOverview, state?.user_session?.user_type, state?.user_session?.__t]);
  const channelVideo = useMemo(() => resolveChannelVideo(channelOverview), [channelOverview]);
  const inviteLink = useMemo(() => {
    const ownerSlug = resolvedOwner || ownerUsername || user_username || creator_username || '';
    const basePath = channelId
      ? `/channel/${ownerSlug}/${channelId}/join`
      : `/channel/${ownerSlug}`;
    if (typeof window === 'undefined') return basePath;
    return `${window.location.origin}${basePath}`;
  }, [channelId, creator_username, ownerUsername, resolvedOwner, user_username]);

  const mediaItems = useMemo(() => {
    if (!Array.isArray(channelMedia)) return [];
    return channelMedia.map((item, idx) => normalizeMediaItem(item, idx)).filter(Boolean);
  }, [channelMedia]);

  const members = useMemo(() => {
    if (!Array.isArray(channelMembers)) return [];
    return channelMembers.map((e, i) => normalizeMember(e, i)).filter(Boolean);
  }, [channelMembers]);

  const refreshMembersList = useCallback(async () => {
    if (!channelId) return false;
    try {
      const { response, data } = await ChannelsService.getMembersList(channelId);
      if (response?.ok) {
        setChannelMembers(Array.isArray(data) ? data : []);
        return true;
      }
    } catch (_) {
      // noop
    }
    return false;
  }, [channelId]);

  const closeMemberRemovalPopup = useCallback(() => {
    if (removingMemberId) return;
    setMemberPendingRemoval(null);
  }, [removingMemberId]);

  const handleToggleMemberMenu = useCallback((memberId) => {
    if (!memberId) return;
    setActiveMemberMenuId((prev) => (prev === memberId ? null : memberId));
  }, []);

  const handleRequestMemberRemoval = useCallback((member) => {
    setActiveMemberMenuId(null);
    setMemberPendingRemoval(member || null);
  }, []);

  const handleConfirmMemberRemoval = useCallback(async () => {
    if (!channelId || !memberPendingRemoval) return;

    const targetMemberId = memberPendingRemoval.userId || memberPendingRemoval.membershipId || memberPendingRemoval.id || null;
    if (!targetMemberId) return;

    setRemovingMemberId(String(targetMemberId));
    try {
      const { response } = await ChannelsService.removeMember(channelId, targetMemberId);
      if (!response?.ok) {
        alert('Could not remove this member right now.');
        return;
      }

      await refreshMembersList();
      setMemberPendingRemoval(null);
    } catch (_) {
      alert('Could not remove this member right now.');
    } finally {
      setRemovingMemberId(null);
    }
  }, [channelId, memberPendingRemoval, refreshMembersList]);

  const closeLeaveChannelPopup = useCallback(() => {
    if (leavingChannel || revokingSubscription) return;
    setLeaveChannelPopupOpen(false);
  }, [leavingChannel, revokingSubscription]);

  const closeRevokeSubscriptionPopup = useCallback(() => {
    if (revokingSubscription) return;
    setRevokeSubscriptionPopupOpen(false);
  }, [revokingSubscription]);

  const performLeaveChannel = useCallback(async () => {
    if (!channelId) return false;
    try {
      const { response } = await ChannelsService.leave(channelId);
      if (!response?.ok && response?.status !== 404) {
        alert('Could not leave this channel right now.');
        return false;
      }

      setIsMemberOfThis(false);
      setHeaderMenuOpen(false);
      setLeaveChannelPopupOpen(false);
      setRevokeSubscriptionPopupOpen(false);
      navigate('/channels');
      return true;
    } catch (_) {
      alert('Could not leave this channel right now.');
      return false;
    }
  }, [channelId, navigate]);

  const handleRequestLeaveChannel = useCallback(() => {
    setHeaderMenuOpen(false);
    setLeaveChannelPopupOpen(true);
  }, []);

  const handleConfirmLeaveChannel = useCallback(async () => {
    if (isPaidChannel && isMember && !canManageChannel) {
      setLeaveChannelPopupOpen(false);
      setRevokeSubscriptionPopupOpen(true);
      return;
    }

    setLeavingChannel(true);
    try {
      await performLeaveChannel();
    } finally {
      setLeavingChannel(false);
    }
  }, [canManageChannel, isMember, isPaidChannel, performLeaveChannel]);

  const handleConfirmRevokeSubscription = useCallback(async () => {
    if (!channelId) return;
    setRevokingSubscription(true);
    try {
      const { response } = await ChannelsService.cancelSubscription(channelId, { immediate: true });
      if (!response?.ok) {
        alert('Could not revoke your subscription right now.');
        return;
      }
      await performLeaveChannel();
    } catch (_) {
      alert('Could not revoke your subscription right now.');
    } finally {
      setRevokingSubscription(false);
    }
  }, [channelId, performLeaveChannel]);

  useEffect(() => {
    if (!activeMemberMenuId) return;

    const handleOutside = (event) => {
      if (event.target.closest('.channel-info__member-actions')) return;
      setActiveMemberMenuId(null);
    };

    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside, { passive: true });
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, [activeMemberMenuId]);

  useEffect(() => {
    if (!headerMenuOpen) return;

    const handleOutside = (event) => {
      if (event.target.closest('.channel-info__header-actions')) return;
      setHeaderMenuOpen(false);
    };

    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside, { passive: true });
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, [headerMenuOpen]);

  const backView = useMemo(() => {
    const queryView = new URLSearchParams(location.search || '').get('view');
    if (queryView === 'posts' || queryView === 'articles') {
      return queryView;
    }
    const stateView = location?.state?.fromVipView;
    if (stateView === 'posts' || stateView === 'articles') {
      return stateView;
    }
    return null;
  }, [location.search, location?.state?.fromVipView]);

  const handleBack = useCallback(() => {
    const ownerSlug = resolvedOwner || ownerUsername || user_username || creator_username || '';
    if (ownerSlug && channelId) {
      const base = `/channel/${ownerSlug}/${channelId}`;
      navigate(backView ? `${base}?view=${backView}` : base);
      return;
    }
    navigate(-1);
  }, [backView, channelId, creator_username, navigate, ownerUsername, resolvedOwner, user_username]);
  const handleOpenPreferences = useCallback(() => {
    if (!resolvedOwner) return;
    const params = new URLSearchParams(location.search || '');
    if (channelId) {
      params.set('channelId', String(channelId));
    }
    const query = params.toString();
    navigate(`/chat/gallery/${resolvedOwner}/preferences${query ? `?${query}` : ''}`, {
      state: { fromChannelInfo: true, channelId: channelId || null },
    });
  }, [channelId, location.search, navigate, resolvedOwner]);

  // ===== Cancel at period end & Renew =====
  const [cancelling, setCancelling] = useState(false);
  const [renewing, setRenewing] = useState(false);

  const cancelAtPeriodEnd = useCallback(async () => {
    if (!channelId) return;
    const confirmed = window.confirm('Auto-renewal will be canceled at the end of the current period. Continue?');
    if (!confirmed) return;
    setCancelling(true);
    try {
      const { response } = await ChannelsService.cancelSubscription(channelId, { /* immediate: false */ });
      if (response?.ok) {
        alert('Done. You will keep access until the current period ends, and you will not be charged again.');
        try {
          const [o, m, membership] = await Promise.all([
            ChannelsService.getOverview(channelId),
            ChannelsService.getMembersList(channelId),
            ChannelsService.getMemberOf('me'),
          ]);
          if (o?.response?.ok) setChannelOverview(o.data);
          if (m?.response?.ok) setChannelMembers(m.data);
          if (membership?.response?.ok) {
            const md = membership.data;
            const list = Array.isArray(md?.data) ? md.data
              : Array.isArray(md?.results) ? md.results
                : Array.isArray(md) ? md : [];
            const memberFlag = list.some((entry) => String(entry?._id ?? entry?.id) === String(channelId));
            setIsMemberOfThis(memberFlag);
          }
        } catch {}
      } else {
        alert('Could not schedule the cancellation.');
      }
    } finally {
      setCancelling(false);
    }
  }, [channelId]);

  const handleRenew = useCallback(async () => {
    if (!channelId) return;
    setRenewing(true);
    try {
      const ownerSlug = resolvedOwner || ownerUsername || user_username || creator_username || '';
      if (!ownerSlug) {
        alert('Unable to start renewal right now. Please try again.');
        return;
      }
      window.location.href = `/channel/${ownerSlug}/${channelId}/join`;
    } catch (err) {
      console.error('channel-info: renew subscription failed', err);
      alert('Unable to start renewal right now. Please try again.');
    } finally {
      setRenewing(false);
    }
  }, [channelId, resolvedOwner, ownerUsername, user_username, creator_username]);

  // Autoplay nudge when the lightbox opens a video
  useEffect(() => {
    if (lightboxOpen && lightboxType === 'video' && lightboxVideoRef.current) {
      const v = lightboxVideoRef.current;
      v.muted = true;      // help autoplay
      v.preload = 'auto';
      const p = v.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    }
  }, [lightboxOpen, lightboxType, lightboxSrc]);

  // HLS loader (on-demand) if browser can't play m3u8 natively
  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!lightboxOpen || lightboxType !== 'video' || !lightboxSrc) return;
      const videoEl = lightboxVideoRef.current;
      if (!videoEl) return;

      const isM3U8 = /\.m3u8(\?|$)/i.test(lightboxSrc);
      const canPlayHlsNatively =
        !!videoEl.canPlayType && videoEl.canPlayType('application/vnd.apple.mpegurl');

      if (isM3U8 && !canPlayHlsNatively) {
        try {
          const { default: Hls } = await import('hls.js/dist/hls.min.js');
          if (!cancelled && Hls && Hls.isSupported()) {
            const h = new Hls();
            h.loadSource(lightboxSrc);
            h.attachMedia(videoEl);
            setHlsInstance(h);
          }
        } catch (_) { /* ignore */ }
      }

      try {
        videoEl.preload = 'auto';
        const p = videoEl.play();
        if (p && typeof p.catch === 'function') p.catch(() => {});
      } catch (_) {}
    })();

    return () => {
      if (hlsInstance) {
        try { hlsInstance.destroy(); } catch {}
        setHlsInstance(null);
      }
      cancelled = true;
    };
  }, [lightboxOpen, lightboxType, lightboxSrc]); // don't include hlsInstance here

  return (
    <div className="channel-info__container">
      <header className="channel-info__header">
        <button type="button" className="channel-info__back-button" onClick={handleBack} title="Back to chat">
          <BackArrowIcon className="channel-info__back-icon" />
        </button>
        <div className="channel-info__header-content">
          <UserProfileImage image={channelAvatar} typeOfProfile={channelOwnerProfileType} className="channel-info__avatar" />
          <div className="channel-info__heading">
            <h1>{channelName}</h1>
            <div className="channel-info__meta">
              {resolvedOwner ? (
                <Link to={`/user/${resolvedOwner}`} className="channel-info__meta-link">
                  @{resolvedOwner}
                </Link>
              ) : null}
            </div>
          </div>
        </div>
        {canManageChannel ? (
          <button
            type="button"
            className="channel-info__preferences-button"
            onClick={handleOpenPreferences}
          >
            <SettingsIcon />
          </button>
        ) : isMember ? (
          <div className={`channel-info__header-actions ${headerMenuOpen ? 'active' : ''}`}>
            <button
              type="button"
              className="channel-info__header-options-button"
              onClick={() => setHeaderMenuOpen((prev) => !prev)}
              aria-label="Channel options"
              title="Channel options"
              disabled={leavingChannel || revokingSubscription}
            >
              <ThreeDotsHorizontal className="channel-info__member-options-icon" />
            </button>
            <div className={`channel-info__header-action-menu ${headerMenuOpen ? 'active' : ''}`}>
              <button
                type="button"
                className="channel-info__member-action-menu-item chat-message__action-menu-item channel-info__header-action-menu-item"
                onClick={handleRequestLeaveChannel}
                disabled={leavingChannel || revokingSubscription}
              >
                <DeleteIcon className="channel-info__member-action-icon chat-message__action-icon" />
                <span>Leave channel</span>
              </button>
            </div>
          </div>
        ) : null}
      </header>

      {loading ? (
        <div className="channel-info__state channel-info__state--loading">
          <Loader inline thin />
        </div>
      ) : error ? (
        <div className="channel-info__state channel-info__state--error">{error}</div>
      ) : (
        <div className="channel-info__content">
          <section className="channel-info__section channel-info__section--type">
            <div className="channel-info__type-content">
              <h2>Type</h2>
              {channelType ? (
                <p className="channel-info__text">{channelType}</p>
              ) : (
                <p className="channel-info__empty">This channel has no type set.</p>
              )}
            </div>
            <button
              type="button"
              className="channel-info__invite-button"
              onClick={() => setInvitePopupOpen(true)}
            >
              Invite
            </button>
          </section>

          <section className="channel-info__section">
            <h2>About</h2>
            {channelDescription ? (
              <p className="channel-info__text">{channelDescription}</p>
            ) : (
              <p className="channel-info__empty">This channel has no description yet.</p>
            )}
            {channelVideo ? (
              <div className="channel-info__video-wrapper">
                <video controls src={channelVideo} preload="metadata" className="channel-info__video" />
              </div>
            ) : null}
          </section>

          {showExpiredNotice && (
            <div className="channel-info__notice channel-info__notice--error">
              <div>
                <strong>Your access has expired.</strong> {expiryLabel ? ('It ended on ' + expiryLabel + '.') : ''}
              </div>
              <button
                type="button"
                className="channel-info__notice-btn"
                onClick={handleRenew}
                disabled={renewing}
              >
                {renewing ? 'Opening checkout…' : 'Renew subscription'}
              </button>
            </div>
          )}

          {showAutoRenewOffNotice && (
            <div className="channel-info__notice channel-info__notice--warn">
              <div>
                {expiryLabel ? ('Auto-renewal is OFF. You will keep access until ' + expiryLabel + '.') : 'Auto-renewal is OFF.'}
              </div>
              <button
                type="button"
                className="channel-info__notice-btn"
                onClick={handleRenew}
                disabled={renewing}
              >
                {renewing ? 'Opening checkout…' : 'Renew now'}
              </button>
            </div>
          )}

          <section className="channel-info__section">
            <div className="channel-info__section-header">
              <h2>
                Shared media
                <span className="channel-info__members-count">
                  {' '}({mediaItems.length})
                </span>
              </h2>
            </div>
            {canViewRestrictedContent ? (
              mediaItems.length ? (
                <div className="channel-info__media-grid">
                  {mediaItems.map((item) => (
                    <div
                      key={item.id}
                      className="channel-info__media-item"
                      role="button"
                      tabIndex={0}
                      onClick={() => openLightbox(item)}
                      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && openLightbox(item)}
                      title="Open file"
                    >
                      {item.type === 'video' ? (
                        <video
                          src={item.playbackUrl || item.previewUrl || item.url}
                          muted
                          playsInline
                          preload="metadata"
                          poster={item.previewUrl || undefined}
                        />
                      ) : (
                        <img
                          src={item.previewUrl || item.url || item.playbackUrl}
                          alt="Shared file"
                          loading="lazy"
                          decoding="async"
                        />
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="channel-info__empty">No shared media yet.</p>
              )
            ) : (
              <p className="channel-info__empty">Subscribe to view shared media.</p>
            )}
          </section>

          <section className="channel-info__section">
            <div className="channel-info__section-header">
              <h2>
                Members
                <span className="channel-info__members-count">
                  {' '}({members.length})
                </span>
              </h2>
            </div>
            {canViewRestrictedContent ? (
              members.length ? (
                <ul className="channel-info__members-list">
                  {members.map((member) => (
                    <li key={member.id} className="channel-info__member">
                      <UserProfileImage
                        image={member.avatar}
                        typeOfProfile={member.profileType}
                        className="channel-info__member-avatar"
                      />
                      <div className="channel-info__member-body">
                        <span className="channel-info__member-name">{member.displayName}</span>
                        <div className="channel-info__member-meta">
                          {member.username ? (
                            <span className="channel-info__member-username">@{member.username}</span>
                          ) : null}
                          {member.role ? (
                            <span className="channel-info__member-role">{member.role}</span>
                          ) : null}
                        </div>
                      </div>
                      {canManageChannel ? (
                        <div className={`channel-info__member-actions ${activeMemberMenuId === member.id ? 'active' : ''}`}>
                          <button
                            type="button"
                            className="channel-info__member-options-button"
                            onClick={() => handleToggleMemberMenu(member.id)}
                            aria-label="Member options"
                            title="Member options"
                            disabled={Boolean(removingMemberId)}
                          >
                            <ThreeDotsHorizontal className="channel-info__member-options-icon" />
                          </button>
                          <div className={`channel-info__member-action-menu ${activeMemberMenuId === member.id ? 'active' : ''}`}>
                            <button
                              type="button"
                              className="channel-info__member-action-menu-item chat-message__action-menu-item"
                              onClick={() => handleRequestMemberRemoval(member)}
                              disabled={
                                Boolean(removingMemberId)
                                || !member.userId
                                || (currentUserId && String(member.userId) === String(currentUserId))
                              }
                            >
                              <DeleteIcon className="channel-info__member-action-icon chat-message__action-icon" />
                              <span>Remove</span>
                            </button>
                          </div>
                        </div>
                      ) : null}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="channel-info__empty">No members yet.</p>
              )
            ) : (
              <p className="channel-info__empty">Subscribe to see the members list.</p>
            )}
          </section>

          {isPaidChannel && isMember && autoRenewOn && !expired && (
            <section className="channel-info__section">
              <h2>Your subscription</h2>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  disabled={cancelling}
                  onClick={cancelAtPeriodEnd}
                  className="channel-info__meta-pill"
                  title="Cancel auto-renewal at the end of the period"
                >
                  {cancelling ? 'Cancelling…' : 'Cancel auto-renewal'}
                </button>
                <span className="channel-info__empty" style={{ marginTop: 4 }}>
                  {expiryLabel ? 'Auto-renewal is ON. Next renewal on ' + expiryLabel + '.' : 'Auto-renewal is ON.'}
                </span>
              </div>
            </section>
          )}
        </div>
      )}

      <InviteChannelPopup
        open={invitePopupOpen}
        close={() => setInvitePopupOpen(false)}
        inviteLink={inviteLink}
        isSubscribers={isSubscribersType && canManageChannel}
      />

      <GenericPopup
        open={Boolean(memberPendingRemoval)}
        className="generic-popup_content channel-info__remove-member-popup"
        opacity
        onBackgroundClick={closeMemberRemovalPopup}
      >
        <div className="generic-popup__header channel-info__remove-member-header">
          <h2 className="generic-popup__header-text channel-info__remove-member-title">Remove member</h2>
          <XIcon className="generic-popup__close channel-info__remove-member-close" onClick={closeMemberRemovalPopup} />
        </div>
        <p className="channel-info__remove-member-text">
          Are you sure you want to remove{' '}
          <span className="channel-info__remove-member-target">
            {memberPendingRemoval?.username ? `@${memberPendingRemoval.username}` : (memberPendingRemoval?.displayName || 'this user')}
          </span>
          ? This action is permanent and the user will no longer be able to see or join your channel.
        </p>
        <div className="channel-info__remove-member-actions-row">
          <ContinueButton
            className="channel-info__remove-member-button channel-info__remove-member-button--confirm"
            onClick={handleConfirmMemberRemoval}
            loading={Boolean(removingMemberId)}
          >
            {removingMemberId ? 'Removing…' : 'Remove'}
          </ContinueButton>
        </div>
      </GenericPopup>

      <GenericPopup
        open={leaveChannelPopupOpen}
        className="generic-popup_content channel-info__action-popup"
        opacity
        onBackgroundClick={closeLeaveChannelPopup}
      >
        <div className="generic-popup__header channel-info__action-header">
          <h2 className="generic-popup__header-text channel-info__action-title">Leave Channel</h2>
          <XIcon className="generic-popup__close channel-info__action-close" onClick={closeLeaveChannelPopup} />
        </div>
        <p className="channel-info__action-text">
          Are you sure you want to leave{' '}
          <span className="channel-info__action-target">
            @{resolvedOwner}&apos;s unique channel
          </span>
          ? You will no longer receive notifications or be able to see new content.
        </p>
        <div className="channel-info__action-actions-row">
          <ContinueButton
            className="channel-info__action-button channel-info__action-button--confirm"
            onClick={handleConfirmLeaveChannel}
            loading={leavingChannel}
          >
            {leavingChannel ? 'Leaving…' : 'Leave Channel'}
          </ContinueButton>
        </div>
      </GenericPopup>

      <GenericPopup
        open={revokeSubscriptionPopupOpen}
        className={`generic-popup_content channel-info__action-popup ${revokingSubscription ? 'channel-info__action-popup--loading' : ''}`}
        opacity
        onBackgroundClick={closeRevokeSubscriptionPopup}
      >
        {revokingSubscription ? (
          <div className="channel-info__action-loader" aria-live="polite" aria-busy="true">
            <Loader inline thin />
          </div>
        ) : (
          <>
            <div className="generic-popup__header channel-info__action-header">
              <h2 className="generic-popup__header-text channel-info__action-title">Revoke subscription</h2>
              <XIcon className="generic-popup__close channel-info__action-close" onClick={closeRevokeSubscriptionPopup} />
            </div>
            <p className="channel-info__action-text">
              {`Cancelling your subscription will stop future auto-renewals. You can always come back later - but ${
                resolvedOwner ? `@${resolvedOwner}` : 'the channel creator'
              } will definitely miss you.`}
            </p>
            <div className="channel-info__action-actions-row">
              <ContinueButton
                className="channel-info__action-button channel-info__action-button--confirm"
                onClick={handleConfirmRevokeSubscription}
              >
                {revokeSubscriptionButtonLabel}
              </ContinueButton>
            </div>
          </>
        )}
      </GenericPopup>

      {/* Lightbox */}
      {lightboxOpen && (
        <div
          className="channel-info__lightbox"
          onClick={closeLightbox}
          role="dialog"
          aria-modal="true"
          aria-label="Media viewer"
        >
          <div
            className="channel-info__lightbox-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="channel-info__lightbox-close"
              onClick={closeLightbox}
              aria-label="Close"
              title="Close"
            >
              ×
            </button>

            {lightboxType === 'video' ? (
              <video
                key={lightboxSrc}                           // re-mount when src changes
                ref={lightboxVideoRef}
                className="channel-info__lightbox-video"
                // If it’s .m3u8, let hls.js attach it. Otherwise, set src directly.
                src={/\.m3u8(\?|$)/i.test(lightboxSrc) ? undefined : lightboxSrc || undefined}
                poster={lightboxPoster || undefined}
                controls
                autoPlay
                playsInline
                preload="auto"
              />
            ) : (
              <img
                className="channel-info__lightbox-image"
                src={lightboxSrc || ''}
                alt="Media"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
